import { useEffect, useRef, useCallback, useState } from "react";
import { useCollaborationStore } from "@/stores/collaboration";
import { logger } from "@/utils";

interface BatteryInfo {
  level: number;
  charging: boolean;
  chargingTime?: number;
  dischargingTime?: number;
}

interface NetworkInfo {
  effectiveType: "2g" | "3g" | "4g" | "slow-2g";
  downlink: number;
  rtt: number;
  saveData: boolean;
}

interface MobileOptimizationState {
  isMobile: boolean;
  isLowPower: boolean;
  isSlowNetwork: boolean;
  batteryLevel: number;
  isCharging: boolean;
  networkType: string;
  optimizationsActive: string[];
}

interface MobileOptimizationActions {
  enableBatteryOptimization: () => void;
  disableBatteryOptimization: () => void;
  adjustForNetworkConditions: () => void;
  pauseCollaboration: () => void;
  resumeCollaboration: () => void;
}

export const useMobileOptimization = () => {
  const {
    batteryOptimization,
    throttleSettings,
    toggleMobileOptimization,
    updateBatteryOptimization,
    updateThrottleSettings,
  } = useCollaborationStore();

  const [batteryInfo, setBatteryInfo] = useState<BatteryInfo | null>(null);
  const [networkInfo, setNetworkInfo] = useState<NetworkInfo | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [activeOptimizations, setActiveOptimizations] = useState<string[]>([]);

  const batteryUpdateTimer = useRef<NodeJS.Timeout | null>(null);
  const networkUpdateTimer = useRef<NodeJS.Timeout | null>(null);
  const visibilityChangeRef = useRef<() => void>();

  /**
   * Detect mobile device
   */
  useEffect(() => {
    const detectMobile = () => {
      const userAgent = navigator.userAgent;
      const isMobileUA =
        /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          userAgent,
        );
      const isTouchDevice =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth <= 768;

      return isMobileUA || (isTouchDevice && isSmallScreen);
    };

    const mobile = detectMobile();
    setIsMobile(mobile);

    if (mobile) {
      toggleMobileOptimization(true);
      logger.info("Mobile device detected, enabling optimizations");
    }
  }, [toggleMobileOptimization]);

  /**
   * Monitor battery status
   */
  useEffect(() => {
    const updateBatteryInfo = (battery: BatteryManager) => {
      const info: BatteryInfo = {
        level: battery.level,
        charging: battery.charging,
        chargingTime: battery.chargingTime,
        dischargingTime: battery.dischargingTime,
      };

      setBatteryInfo(info);

      // Enable battery optimization if level is low
      if (info.level < 0.2 && !info.charging) {
        enableBatteryOptimization();
      } else if (info.level > 0.8 || info.charging) {
        // Consider disabling optimizations if battery is good
        if (batteryOptimization.enabled) {
          logger.info(
            "Battery level good, considering disabling optimizations",
          );
        }
      }
    };

    const initBattery = async () => {
      try {
        if (navigator.getBattery) {
          const battery = await navigator.getBattery();

          updateBatteryInfo(battery);

          // Listen for battery changes
          battery.addEventListener("levelchange", () =>
            updateBatteryInfo(battery),
          );
          battery.addEventListener("chargingchange", () =>
            updateBatteryInfo(battery),
          );

          // Periodic updates
          batteryUpdateTimer.current = setInterval(() => {
            updateBatteryInfo(battery);
          }, 60000); // Every minute
        }
      } catch (error) {
        logger.warn("Battery API not available:", error);
      }
    };

    if (isMobile) {
      initBattery();
    }

    return () => {
      if (batteryUpdateTimer.current) {
        clearInterval(batteryUpdateTimer.current);
      }
    };
  }, [isMobile, batteryOptimization.enabled]);

  /**
   * Monitor network conditions
   */
  useEffect(() => {
    const updateNetworkInfo = () => {
      try {
        const connection =
          navigator.connection ||
          navigator.mozConnection ||
          navigator.webkitConnection;

        if (connection) {
          const info: NetworkInfo = {
            effectiveType: connection.effectiveType,
            downlink: connection.downlink,
            rtt: connection.rtt,
            saveData: connection.saveData,
          };

          setNetworkInfo(info);

          // Adjust based on network conditions
          adjustForNetworkConditions(info);
        }
      } catch (error) {
        logger.warn("Network Information API not available:", error);
      }
    };

    if (isMobile) {
      updateNetworkInfo();

      // Listen for network changes
      window.addEventListener("online", updateNetworkInfo);
      window.addEventListener("offline", updateNetworkInfo);

      // Periodic updates
      networkUpdateTimer.current = setInterval(updateNetworkInfo, 30000); // Every 30 seconds
    }

    return () => {
      window.removeEventListener("online", updateNetworkInfo);
      window.removeEventListener("offline", updateNetworkInfo);

      if (networkUpdateTimer.current) {
        clearInterval(networkUpdateTimer.current);
      }
    };
  }, [isMobile]);

  /**
   * Handle visibility change (app backgrounding)
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // App went to background
        if (batteryOptimization.backgroundSync) {
          pauseCollaboration();
          logger.info("App backgrounded, pausing collaboration");
        }
      } else {
        // App came to foreground
        if (isPaused) {
          resumeCollaboration();
          logger.info("App foregrounded, resuming collaboration");
        }
      }
    };

    visibilityChangeRef.current = handleVisibilityChange;
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isPaused, batteryOptimization.backgroundSync]);

  /**
   * Enable battery optimization
   */
  const enableBatteryOptimization = useCallback(() => {
    updateBatteryOptimization({
      enabled: true,
      reducedUpdates: true,
    });

    // Increase throttle delays
    updateThrottleSettings({
      cursorUpdateDelay: 200, // Slower cursor updates
      presenceUpdateDelay: 5000, // Much slower presence updates
      operationBatchSize: 20, // Larger batches
      operationBatchDelay: 500, // Longer delays
    });

    setActiveOptimizations((prev) => [
      ...prev.filter((o) => o !== "battery"),
      "battery",
    ]);
    logger.info("Battery optimization enabled");
  }, [updateBatteryOptimization, updateThrottleSettings]);

  /**
   * Disable battery optimization
   */
  const disableBatteryOptimization = useCallback(() => {
    updateBatteryOptimization({
      enabled: false,
      reducedUpdates: false,
    });

    // Reset to mobile-optimized but not battery-constrained settings
    updateThrottleSettings({
      cursorUpdateDelay: 120,
      presenceUpdateDelay: 2000,
      operationBatchSize: 10,
      operationBatchDelay: 200,
    });

    setActiveOptimizations((prev) => prev.filter((o) => o !== "battery"));
    logger.info("Battery optimization disabled");
  }, [updateBatteryOptimization, updateThrottleSettings]);

  /**
   * Adjust for network conditions
   */
  const adjustForNetworkConditions = useCallback(
    (network?: NetworkInfo) => {
      const netInfo = network || networkInfo;
      if (!netInfo) return;

      let optimizations = activeOptimizations.filter((o) => o !== "network");

      if (
        netInfo.effectiveType === "2g" ||
        netInfo.effectiveType === "slow-2g" ||
        netInfo.saveData
      ) {
        // Very slow network - aggressive optimization
        updateThrottleSettings({
          cursorUpdateDelay: 500, // Very slow cursor updates
          presenceUpdateDelay: 10000, // Very slow presence updates
          operationBatchSize: 50, // Large batches
          operationBatchDelay: 1000, // Long delays
        });

        optimizations.push("network");
        logger.info("Slow network detected, enabling aggressive optimization");
      } else if (netInfo.effectiveType === "3g") {
        // Moderate network - some optimization
        updateThrottleSettings({
          cursorUpdateDelay: 200,
          presenceUpdateDelay: 3000,
          operationBatchSize: 15,
          operationBatchDelay: 300,
        });

        optimizations.push("network");
        logger.info("3G network detected, enabling moderate optimization");
      } else if (netInfo.downlink < 1) {
        // Low bandwidth - some optimization
        updateThrottleSettings({
          cursorUpdateDelay: 150,
          presenceUpdateDelay: 2500,
          operationBatchSize: 12,
          operationBatchDelay: 250,
        });

        optimizations.push("network");
        logger.info("Low bandwidth detected, enabling optimization");
      }

      setActiveOptimizations(optimizations);
    },
    [networkInfo, activeOptimizations, updateThrottleSettings],
  );

  /**
   * Pause collaboration features
   */
  const pauseCollaboration = useCallback(() => {
    setIsPaused(true);

    // Stop non-essential updates
    updateThrottleSettings({
      cursorUpdateDelay: 10000, // Very slow
      presenceUpdateDelay: 30000, // Very slow
    });

    setActiveOptimizations((prev) => [
      ...prev.filter((o) => o !== "paused"),
      "paused",
    ]);
  }, [updateThrottleSettings]);

  /**
   * Resume collaboration features
   */
  const resumeCollaboration = useCallback(() => {
    setIsPaused(false);

    // Resume normal mobile-optimized settings
    adjustForNetworkConditions();

    if (batteryInfo && batteryInfo.level < 0.2 && !batteryInfo.charging) {
      enableBatteryOptimization();
    } else {
      // Use standard mobile settings
      updateThrottleSettings({
        cursorUpdateDelay: 120,
        presenceUpdateDelay: 2000,
      });
    }

    setActiveOptimizations((prev) => prev.filter((o) => o !== "paused"));
  }, [
    adjustForNetworkConditions,
    batteryInfo,
    enableBatteryOptimization,
    updateThrottleSettings,
  ]);

  /**
   * Get optimization recommendations
   */
  const getOptimizationRecommendations = useCallback((): string[] => {
    const recommendations = [];

    if (
      batteryInfo &&
      batteryInfo.level < 0.3 &&
      !batteryInfo.charging &&
      !batteryOptimization.enabled
    ) {
      recommendations.push(
        "Enable battery optimization to extend battery life",
      );
    }

    if (
      networkInfo &&
      (networkInfo.effectiveType === "2g" || networkInfo.saveData) &&
      !activeOptimizations.includes("network")
    ) {
      recommendations.push(
        "Network optimization recommended for slow connection",
      );
    }

    if (isMobile && activeOptimizations.length === 0) {
      recommendations.push(
        "Consider enabling mobile optimizations for better performance",
      );
    }

    return recommendations;
  }, [
    batteryInfo,
    networkInfo,
    isMobile,
    batteryOptimization.enabled,
    activeOptimizations,
  ]);

  const state: MobileOptimizationState = {
    isMobile,
    isLowPower: batteryInfo
      ? batteryInfo.level < 0.2 && !batteryInfo.charging
      : false,
    isSlowNetwork: networkInfo
      ? networkInfo.effectiveType === "2g" ||
        networkInfo.effectiveType === "slow-2g"
      : false,
    batteryLevel: batteryInfo?.level || 1,
    isCharging: batteryInfo?.charging || false,
    networkType: networkInfo?.effectiveType || "unknown",
    optimizationsActive: activeOptimizations,
  };

  const actions: MobileOptimizationActions = {
    enableBatteryOptimization,
    disableBatteryOptimization,
    adjustForNetworkConditions,
    pauseCollaboration,
    resumeCollaboration,
  };

  return {
    ...state,
    ...actions,
    batteryInfo,
    networkInfo,
    isPaused,
    getOptimizationRecommendations,
  };
};
